import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { colors } from '@/constants/colors';
import {
   cpuPercent,
   memPercent,
   humanKb,
   phonesLine,
   spaceRow,
   sysState,
   memTone,
   tone,
   type RawSys,
   type Tone,
} from '@/utils/systemInfo';
import {
   outageLine,
   reportedAgo,
   restartLine,
} from './deviceDiagText';

/**
 * The back of the device card: what the router says about its own condition.
 *
 * WHY A CARD FACE AND NOT A TAB: an operator asking "why is this vendo slow"
 * is already looking at the card. Everything here is a fact the router already
 * volunteers on its heartbeat, so there is nothing to fetch and nothing to ask
 * of the vendo — the card simply turns over.
 *
 * 🔴 THE ONE RULE: a router that DID NOT SAY must never render as a router that
 * said ZERO. `diag.freeKb` defaults to 0, and on 5D ALCARDE that made a router
 * which had never reported look like a router out of disk space — two days of
 * chasing a full disk that was never full. Every value here comes back as null
 * from utils/systemInfo when it was not reported, and a null value gets a
 * sentence, never a figure.
 *
 * NO TEMPERATURE ROW. The Ruijie has no thermal sensor — no thermal_zone at all
 * — so it was cut rather than shipped permanently blank. Four honest readings
 * beat five with one that never fills in.
 */

export interface SystemInfoFaceProps {
   deviceId: string;
   /** The router's live readings. Null on a router that has never sent any. */
   sys?: RawSys | null;
   /** The self-report, which has carried free space and restarts since 2.1.6. */
   diag?: {
      freeKb?: number | null;
      reboots24h?: number;
      outage?: string;
   } | null;
   /** When the ROUTER last spoke — never when the reading was taken. A stale
    *  report always looks perfectly healthy to itself. */
   lastSeenAt?: string | null;
   /** Which hardware this is. A MikroTik can never report these numbers — see
    *  the `unsupported` branch below, and sysState for why it matters. */
   platform?: string | null;
   onBack: () => void;
   /** Inside another window (the device details sheet): no head / back button. */
   embedded?: boolean;
   /** Rings sweep on open. Off while the face is hidden so the animation plays
    *  when it is actually looked at, and skipped for reduced-motion. */
   active: boolean;
}

export default function SystemInfoFace({
   deviceId,
   sys,
   diag,
   lastSeenAt,
   platform,
   onBack,
   active,
   embedded = false,
}: SystemInfoFaceProps) {
   const state = sysState(sys, diag?.freeKb, platform);

   const cpu = cpuPercent(sys?.loadCenti, sys?.cores, sys?.busyPct);
   const mem = memPercent(sys?.memTotalKb, sys?.memAvailKb);

   // Settings space prefers the live reading (which carries a total, so it can
   // draw a bar) and falls back to the self-report, which most of the fleet has
   // been sending since 2.1.6 with no total beside it.
   //
   // 🔴 `diag.freeKb` DEFAULTS TO 0 FOR EVERY ROUTER THAT PREDATES IT, so only a
   // positive number counts as a reading. Zero there means "did not say"; taking
   // it literally is the 5D ALCARDE mistake.
   const cfgFree =
      sys?.cfgFreeKb ?? (diag?.freeKb && diag.freeKb > 0 ? diag.freeKb : null);
   const cfg = spaceRow(cfgFree, sys?.cfgTotalKb);
   const dl = spaceRow(sys?.dlFreeKb, sys?.dlTotalKb);

   const restart = outageLine(diag?.outage);
   const restarts = restartLine(diag?.reboots24h);
   const phones = phonesLine(sys?.clients);

   return (
      <Face>
         {!embedded && (
         <Head>
            <BackBtn onClick={onBack} title="Back to the vendo" aria-label="Back">
               ‹
            </BackBtn>
            <div>
               <H3>System Info</H3>
               <Sub>
                  {deviceId} · {reportedAgo(lastSeenAt)}
               </Sub>
            </div>
         </Head>
         )}

         {/* A router too old to report any of this. It is told plainly, and the
             card says what still works — an operator seeing an empty panel
             assumes their vendo is broken, which is the opposite of true. */}
         {state === 'silent' && (
            <Stale>
               This router is too old to report its system info. It needs a
               router firmware update. Everything else on this vendo works
               normally.
            </Stale>
         )}

         {/* 🔴 NOT "too old". A MikroTik reports none of this and never will —
             these numbers ride the OpenWRT agent's heartbeat, and a MikroTik
             runs no agent of ours. Measured on prod 2026-08-30: 68 main vendos
             are MikroTik, and every one of them would have been told to install
             a firmware update that does not exist and could not help. */}
         {state === 'unsupported' && (
            <Stale>
               A <strong>MikroTik</strong> router is read every 5 minutes over
               its remote link. No reading yet — it appears as soon as the
               router answers. Everything else on this vendo works normally.
            </Stale>
         )}

         {state === 'live' && (
            <Vitals>
               <Vital>
                  <Ring pct={cpu} animate={active} />
                  <div>
                     <VitalLabel>CPU</VitalLabel>
                     {/* 🔴 THE CORE COUNT IS NOT DECORATION. A load of 0.47 on
                         4 CPUs is about 12% busy, and shown alone it reads as
                         47%. The ring holds the honest percentage; this line
                         shows the raw pair it was computed from. */}
                     <VitalSub>
                        {sys?.loadCenti != null && sys?.cores != null
                           ? `load ${(sys.loadCenti / 100).toFixed(2)} · ${sys.cores} core${sys.cores === 1 ? '' : 's'}`
                           : 'not reported'}
                     </VitalSub>
                  </div>
               </Vital>
               <Vital>
                  {/* Memory is coloured on its OWN bands (memTone), not the
                      processor's. A healthy Ruijie idles near 70% used and that
                      must read as green — see memTone for the measurements. */}
                  <Ring pct={mem} animate={active} toneOf={memTone} />
                  <div>
                     <VitalLabel>Memory</VitalLabel>
                     <VitalSub>
                        {sys?.memAvailKb != null && sys?.memTotalKb != null
                           ? `${humanKb(sys.memAvailKb)} of ${humanKb(sys.memTotalKb)} available`
                           : 'not reported'}
                     </VitalSub>
                  </div>
               </Vital>
            </Vitals>
         )}

         {/* The middle of the fleet: it reports its space and its restarts but
             knows nothing about CPU. Saying so beats a blank half-card, and
             beats hiding the rows below that we genuinely have. */}
         {state === 'partial' && (
            <Stale>
               This router does not report its processor or memory yet — it needs
               a router firmware update. What it does report is below.
            </Stale>
         )}

         {/* The rows are hidden for a MikroTik. Four lines of "did not say"
             under a message explaining it never says anything is noise, and it
             reads like a fault rather than a hardware difference. */}
         {state !== 'unsupported' && (
         <Rows>
            {/* SETTINGS SPACE FIRST, and that order is the point of the row.
                It is the small one — about 5.5MB — and a full one makes a router
                refuse updates in silence. It is the only figure here that
                predicts a stuck router. */}
            <Row>
               <K>
                  Settings space
                  <KSub>where rates and theme live</KSub>
               </K>
               <V>
                  {cfg ? (
                     <>
                        {cfg.text}
                        {cfg.usedPct !== null && (
                           <Bar>
                              <BarFill
                                 style={{ width: `${cfg.usedPct}%` }}
                                 $tone={cfg.tone}
                              />
                           </Bar>
                        )}
                     </>
                  ) : (
                     <NotSaid>did not say</NotSaid>
                  )}
               </V>
            </Row>

            <Row>
               <K>
                  Download space
                  <KSub>where updates land</KSub>
               </K>
               <V>
                  {dl ? (
                     <>
                        {dl.text}
                        {dl.usedPct !== null && (
                           <Bar>
                              <BarFill
                                 style={{ width: `${dl.usedPct}%` }}
                                 $tone={dl.tone}
                              />
                           </Bar>
                        )}
                     </>
                  ) : (
                     <NotSaid>did not say</NotSaid>
                  )}
               </V>
            </Row>

            <Row>
               <K>Last restart</K>
               <V>
                  {restart ? (
                     <>
                        {/* Deliberately NOT "power cut". The router cannot tell
                            a power cut from a person from a crash, and only says
                            so when it genuinely can (its own watchdog, or a
                            restart we sent). Naming one cause would be a guess
                            printed as a fact, on the one screen built to end the
                            guessing.

                            Set in normal weight, unlike every other value here,
                            because it is a SENTENCE and not a figure. In the row
                            style's bold it wrapped to three lines and shouted
                            down the storage numbers above it, which are the ones
                            that actually predict a stuck router. */}
                        <Sentence>{restart}</Sentence>
                        {restarts && <VSub>{restarts}</VSub>}
                     </>
                  ) : restarts ? (
                     <Sentence>{restarts}</Sentence>
                  ) : (
                     <NotSaid>nothing recorded</NotSaid>
                  )}
               </V>
            </Row>

            <Row>
               <K>Connected phones</K>
               <V>{phones ?? <NotSaid>did not say</NotSaid>}</V>
            </Row>
         </Rows>
         )}

         <Note>
            Sent by the router on its own check-in. Nothing is asked of the
            vendo.
            {dl && (
               <>
                  {' '}
                  Download space is a RAM disk, so it shares the memory above
                  rather than being separate storage.
               </>
            )}
         </Note>
      </Face>
   );
}

/* ------------------------------------------------------------------ ring -- */

const R = 26;
const C = 2 * Math.PI * R;

/**
 * The sweeping dial.
 *
 * A null percentage draws an empty track and says so in words rather than
 * animating to zero — a ring that sweeps to 0% is indistinguishable from a
 * router reporting an idle processor, and this whole card exists to keep those
 * two apart.
 */
function Ring({
   pct,
   animate,
   toneOf = tone,
}: {
   pct: number | null;
   animate: boolean;
   /** How this particular reading should be coloured. Memory uses its own
    *  bands; everything else uses the default. */
   toneOf?: (pct: number | null) => Tone;
}) {
   const [shown, setShown] = useState(0);

   useEffect(() => {
      if (pct === null) {
         setShown(0);
         return;
      }
      // Respect the viewer's motion setting — and matchMedia is guarded because
      // jsdom does not implement it, which would take the mount test down with
      // an error that has nothing to do with this component.
      const reduce =
         typeof window !== 'undefined' &&
         typeof window.matchMedia === 'function' &&
         window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (!animate || reduce) {
         setShown(pct);
         return;
      }
      setShown(0);
      const id = window.setTimeout(() => setShown(pct), 60);
      return () => window.clearTimeout(id);
   }, [pct, animate]);

   const stroke =
      pct === null
         ? colors.GRAY12
         : ({ ok: colors.SUCCESS, warn: colors.WARNING, bad: colors.DANGER } as const)[
              toneOf(pct)
           ];

   return (
      <RingBox>
         <svg width="62" height="62" viewBox="0 0 62 62" aria-hidden="true">
            <circle
               cx="31"
               cy="31"
               r={R}
               fill="none"
               strokeWidth="7"
               stroke={colors.GRAY12}
            />
            <RingFill
               cx="31"
               cy="31"
               r={R}
               fill="none"
               strokeWidth="7"
               strokeLinecap="round"
               stroke={stroke}
               strokeDasharray={C}
               strokeDashoffset={C * (1 - shown / 100)}
            />
         </svg>
         <RingNum>{pct === null ? '—' : `${Math.round(shown)}%`}</RingNum>
      </RingBox>
   );
}

/* ---------------------------------------------------------------- styles -- */

const Face = styled.div`
   display: flex;
   flex-direction: column;
   gap: 10px;
   min-width: 0;
`;

const Head = styled.div`
   display: flex;
   align-items: center;
   gap: 10px;
`;

const BackBtn = styled.button`
   background: ${colors.GRAY14};
   border: 1px solid ${colors.GRAY12};
   border-radius: 8px;
   width: 30px;
   height: 30px;
   min-width: 30px;
   font-size: 15px;
   line-height: 1;
   cursor: pointer;
   color: ${colors.GRAY1};
   display: grid;
   place-items: center;
   &:hover {
      background: ${colors.PRIMARY}12;
      color: ${colors.PRIMARY};
   }
`;

const H3 = styled.h3`
   margin: 0;
   font-size: 14px;
   font-weight: 700;
   color: ${colors.GRAY1};
`;

const Sub = styled.div`
   font-size: 11px;
   color: ${colors.GRAY3};
`;

const Vitals = styled.div`
   display: grid;
   /* Set explicitly. An auto-fit track with a min width silently collapses to
      one column inside a narrow card, and these two belong side by side. */
   grid-template-columns: 1fr 1fr;
   gap: 8px;
   @media (max-width: 420px) {
      grid-template-columns: 1fr;
   }
`;

const Vital = styled.div`
   border: 1px solid ${colors.GRAY12};
   border-radius: 10px;
   padding: 10px;
   display: flex;
   align-items: center;
   gap: 10px;
   min-width: 0;
`;

const RingBox = styled.div`
   width: 62px;
   height: 62px;
   flex: 0 0 62px;
   position: relative;
   svg {
      transform: rotate(-90deg);
      display: block;
   }
`;

const RingFill = styled.circle`
   transition: stroke-dashoffset 1.1s cubic-bezier(0.3, 0.9, 0.3, 1),
      stroke 0.4s;
   @media (prefers-reduced-motion: reduce) {
      transition: none;
   }
`;

const RingNum = styled.div`
   position: absolute;
   inset: 0;
   display: grid;
   place-items: center;
   font-size: 13px;
   font-weight: 700;
   font-variant-numeric: tabular-nums;
   color: ${colors.GRAY1};
`;

const VitalLabel = styled.div`
   font-size: 10px;
   color: ${colors.GRAY3};
   text-transform: uppercase;
   letter-spacing: 0.04em;
`;

const VitalSub = styled.div`
   font-size: 11.5px;
   color: ${colors.GRAY1};
   margin-top: 2px;
`;

const Rows = styled.div`
   border: 1px solid ${colors.GRAY12};
   border-radius: 10px;
   overflow: hidden;
`;

const Row = styled.div`
   display: flex;
   align-items: center;
   justify-content: space-between;
   gap: 10px;
   padding: 8px 10px;
   & + & {
      border-top: 1px solid ${colors.GRAY12};
   }
`;

const K = styled.div`
   font-size: 12px;
   color: ${colors.GRAY3};
   min-width: 0;
`;

const KSub = styled.span`
   display: block;
   font-size: 10.5px;
   color: ${colors.GRAY5};
`;

const V = styled.div`
   font-size: 12.5px;
   font-weight: 600;
   text-align: right;
   color: ${colors.GRAY1};
   min-width: 0;
`;

/** A value that is prose rather than a number — see the restart row. */
const Sentence = styled.span`
   display: block;
   font-weight: 400;
   font-size: 11.5px;
   line-height: 1.35;
   color: ${colors.GRAY1};
`;

const VSub = styled.small`
   display: block;
   font-weight: 400;
   font-size: 11px;
   color: ${colors.GRAY3};
`;

/**
 * "did not say" — quiet on purpose.
 *
 * It is not a fault and must not look like one: a red or bold treatment here
 * would read as "this vendo has a problem" on ~48 perfectly healthy routers
 * that are simply on older firmware.
 */
const NotSaid = styled.span`
   font-weight: 400;
   font-style: italic;
   color: ${colors.GRAY5};
`;

/**
 * The track behind the fill.
 *
 * 🔴 `GRAY14` (--app-surface-muted, #f5f5f5) IS INVISIBLE ON THE CARD, which is
 * also white. With a nearly-empty settings area the 7% fill then rendered as a
 * stray green dot floating under the text with no track around it — it did not
 * read as a bar at all. Caught by looking at the built page, not by any test.
 * `GRAY10` is the strong border token and holds up in both themes.
 */
const Bar = styled.div`
   height: 5px;
   border-radius: 999px;
   background: ${colors.GRAY10};
   overflow: hidden;
   margin-top: 4px;
   width: 110px;
   margin-left: auto;
`;

const BarFill = styled.i<{ $tone: Tone }>`
   display: block;
   height: 100%;
   border-radius: 999px;
   background: ${({ $tone }) =>
      $tone === 'bad'
         ? colors.DANGER
         : $tone === 'warn'
           ? colors.WARNING
           : colors.SUCCESS};
   transition: width 1.1s cubic-bezier(0.3, 0.9, 0.3, 1);
   @media (prefers-reduced-motion: reduce) {
      transition: none;
   }
`;

const Stale = styled.div`
   background: ${colors.WARNING}18;
   border: 1px solid ${colors.WARNING}55;
   border-radius: 8px;
   padding: 8px 10px;
   font-size: 11.5px;
   color: ${colors.GRAY1};
`;

const Note = styled.div`
   margin-top: auto;
   padding-top: 8px;
   font-size: 10.5px;
   color: ${colors.GRAY5};
`;
